import { ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'

import { ArrowCircleRightIcon } from '@10xLabs/react-web/components/Icons/ArrowCircleRight'
import { Dropdown } from 'components/v2/Dropdown'
import { StyledLink } from 'components/v2/Link'
import { LabelWrapper, Label, ReturnIcon } from '../Service'
import { CheckedLuggageIcon } from 'components/Icons/CheckedLuggageIcon'
import { HandLuggageIcon } from 'components/Icons/HandLuggageIcon'
import styled from 'styled-components'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { NavLink } from 'react-router-dom'
import { GTBookingV2TicketAncillaryType } from 'gql/types.generated'
import { isTicketCancelled } from 'resolvers/bookings'
import {
  IBookingAncillariesDropdownProps,
  IBookingAncillariesProps
} from 'features/Bookings/components/BookingAncillaries/types'
import { TicketInsuranceStatus } from 'features/Bookings/components/TicketInsuranceStatus'
import { getStatusByTickets } from 'resolvers/insurance'
import { getAncillariesByType, calculateAncillariesTotals } from 'models/bookings'

const BookingAncillariesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`
const Container = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`
const Content = styled.div`
  display: flex;
  row-gap: 16px;
  flex-direction: column;
`
const Row = styled.div`
  display: flex;
  justify-content: space-between;
`
const Item = styled.div`
  display: flex;
  gap: 16px;
`
const Title = styled.div`
  color: #5d5c70;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`
const CountContainer = styled.div`
  display: flex;
  gap: 8px;
`

const BookingAncillariesDropdown = ({ booking, tickets, isReturn }: IBookingAncillariesDropdownProps): ReactElement => {
  const luggageAncillaries = tickets.flatMap((ticket) =>
    getAncillariesByType(ticket, GTBookingV2TicketAncillaryType.Luggage)
  )
  const carryOnAncillaries = tickets.flatMap((ticket) =>
    getAncillariesByType(ticket, GTBookingV2TicketAncillaryType.CarryOn)
  )
  const insuranceAncillaries = tickets.flatMap((ticket) =>
    getAncillariesByType(ticket, GTBookingV2TicketAncillaryType.Insurance)
  )
  const preferredSeatAncillaries = tickets.flatMap((ticket) =>
    getAncillariesByType(ticket, GTBookingV2TicketAncillaryType.Seat)
  )

  const luggageQtys = calculateAncillariesTotals(luggageAncillaries)
  const carryOnQtys = calculateAncillariesTotals(carryOnAncillaries)
  const insuranceQtys = calculateAncillariesTotals(insuranceAncillaries)
  const preferredSeatQtys = calculateAncillariesTotals(preferredSeatAncillaries)

  const totalQtys = {
    qty: luggageQtys.qty + carryOnQtys.qty + insuranceQtys.qty + preferredSeatQtys.qty,
    amount: luggageQtys.amount + carryOnQtys.amount + insuranceQtys.amount + preferredSeatQtys.amount
  }

  return (
    <Dropdown toggle={<StyledLink>{totalQtys.qty}</StyledLink>} noPadding fitContent>
      <Container>
        <Title>
          {isReturn ? (
            <LabelWrapper>
              <ReturnIcon />
              <Label>
                <FormattedMessage id="dictionary.return" defaultMessage="Return" />
              </Label>
            </LabelWrapper>
          ) : (
            <LabelWrapper>
              <ArrowCircleRightIcon />
              <Label>
                <FormattedMessage id="dictionary.departure" defaultMessage="Departure" />
              </Label>
            </LabelWrapper>
          )}
        </Title>
        <Content>
          <Row>
            <Item>
              <FormattedMessage id="dictionary.luggages" defaultMessage="Luggages" />
              <CountContainer>
                <span>{luggageQtys.qty}</span>
                <CheckedLuggageIcon />
              </CountContainer>
              <CountContainer>
                <span>{carryOnQtys.qty}</span>
                <HandLuggageIcon />
              </CountContainer>
            </Item>
            <Item>
              <FormattedPrice value={luggageQtys.amount + carryOnQtys.amount} />
            </Item>
          </Row>
          <Row>
            <Item>
              <FormattedMessage id="booking.list.dropdown.travelAssistance" defaultMessage="Travel Assistance" />
              <span>
                <StyledLink as={NavLink} to={`/ancillaries/insurance?reservation=${booking.id}`}>
                  <span>{insuranceQtys.qty}</span>
                </StyledLink>
              </span>
              <TicketInsuranceStatus status={getStatusByTickets(tickets)} />
            </Item>
            <Item>
              <FormattedPrice value={insuranceQtys.amount} />
            </Item>
          </Row>
          <Row>
            <Item>
              <FormattedMessage id="booking.list.dropdown.preferredSeats" defaultMessage="Preferred Seats" />
              <span>{preferredSeatQtys.qty}</span>
            </Item>
            <Item>
              <FormattedPrice value={preferredSeatQtys.amount} />
            </Item>
          </Row>
          <Row>
            <Item>
              <FormattedMessage id="dictionary.total" defaultMessage="Total" />
            </Item>
            <Item>
              <FormattedPrice value={totalQtys.amount} />
            </Item>
          </Row>
        </Content>
      </Container>
    </Dropdown>
  )
}

const BookingAncillaries = ({
  booking,
  showReturnTrips,
  showDepartureTrips,
  departureTrips,
  returnTrips,
  tickets
}: IBookingAncillariesProps): ReactElement => {
  const departureTrip = departureTrips[0]
  const returnTrip = returnTrips[0]
  const departureTickets = tickets.filter(
    (ticket) => ticket.trip?.id === departureTrip?.id && !isTicketCancelled(ticket)
  )
  const returnTickets = tickets.filter((ticket) => ticket.trip?.id === returnTrip?.id && !isTicketCancelled(ticket))
  const showTripLabel = showDepartureTrips && showReturnTrips

  return (
    <BookingAncillariesContainer>
      {showDepartureTrips ? (
        <div>
          {showTripLabel ? (
            <LabelWrapper>
              <ArrowCircleRightIcon />
              <Label>
                <FormattedMessage id="dictionary.departure" defaultMessage="Departure" />
              </Label>
            </LabelWrapper>
          ) : null}
          <BookingAncillariesDropdown booking={booking} tickets={departureTickets} />
        </div>
      ) : null}
      {showReturnTrips ? (
        <div>
          {showTripLabel ? (
            <LabelWrapper>
              <ReturnIcon />
              <Label>
                <FormattedMessage id="dictionary.return" defaultMessage="Return" />
              </Label>
            </LabelWrapper>
          ) : null}
          <BookingAncillariesDropdown booking={booking} tickets={returnTickets} isReturn />
        </div>
      ) : null}
    </BookingAncillariesContainer>
  )
}

export default BookingAncillaries
